import { CustomEventWithDetail, html, type Html, on, Page, View } from 'rune-ts';
import klass from './HandmadeCreatorLanding.module.scss';
import motion_klass from './views/MotionSection/MotionSection.module.scss';
import scale_klass from './views/ScaleSection/ScaleSection.module.scss';
import video_klass from './views/VideoSection/VideoSection.module.scss';
import link_klass from './views/LinksSection/LinksSection.module.scss';
import story_klass from './views/StorySection/StorySection.module.scss';
import floatKlass from '../../components/cells/FloatingBox/FloatingButtons/FloatingButton.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { footer_links } from '../../components/cells/Footer/constant';
import {
  HandmadeCreatorHeader,
  MotionSection,
  StatisticsSection,
  PortfolioSection,
  InterviewSection,
  BottomSection,
  StorySection,
  LinksSection,
  QuestionSection,
  ScaleSection,
  CraftSection,
  VideoSection,
  NavigateToStorySectionCard,
  toggleHeaderMenu,
  navigateSection,
} from './views';
import { ButtonCtaLanding } from '../../../shared/components/atoms/ButtonCtaLanding/ButtonCtaLanding';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger.js';
import ScrollToPlugin from 'gsap/dist/ScrollToPlugin.js';
import ScrollSmoother from 'gsap/dist/ScrollSmoother.js';
import _ScrollSmoother from 'gsap/ScrollSmoother';
import { DEFAULT_ANIMATION_DURATION, ONE_SCROLL_INDEX_MAP_MO, ONE_SCROLL_INDEX_MAP_PC } from './const';
import { pipe, throwIf, isNull, filter, take, toArray } from '@fxts/core';
import { FloatingBottomContainer } from '../../components/cells/FloatingBottomContainer/FloatingBottomContainer';

export class PauseScrollSmootherEvent extends CustomEventWithDetail<{ is_paused: boolean }> {}
export class SetScrollIndexEvent extends CustomEventWithDetail<{ current_index: number }> {}
export interface ScrollState {
  current_index: number;
  is_animating: boolean;
  is_scrollbar_moving: boolean;
}
export interface HandmadeCreatorLandingPageData {
  layout_options: BaseLayoutOptions;
}

type ScrollPosition = { klass: string; is_scrolled?: boolean };

const scroll_position_list: Record<number, ScrollPosition> = {
  0: { klass: motion_klass.motion, is_scrolled: false },
  1: { klass: scale_klass.container, is_scrolled: false },
  2: { klass: scale_klass.container, is_scrolled: true },
  3: { klass: 'craft_1', is_scrolled: false },
  4: { klass: 'craft_2', is_scrolled: false },
  5: { klass: 'craft_3', is_scrolled: false },
  6: { klass: 'video_1', is_scrolled: false },
  7: { klass: 'video_1', is_scrolled: true },
  8: { klass: 'video_2', is_scrolled: false },
  9: { klass: 'video_3', is_scrolled: false },
  10: { klass: link_klass.links, is_scrolled: false },
};

const scroll_position_list_mo: Record<number, ScrollPosition> = {
  0: { klass: motion_klass.motion },
  1: { klass: scale_klass.container },
  2: { klass: scale_klass.container },
  3: { klass: 'craft_video_1' },
  4: { klass: 'craft_content_1' },
  5: { klass: 'craft_video_2' },
  6: { klass: 'craft_content_2' },
  7: { klass: 'craft_video_3' },
  8: { klass: 'craft_content_3' },
  9: { klass: 'video_1' },
  10: { klass: 'video_1' },
  11: { klass: 'video_1' },
  12: { klass: 'video_2' },
  13: { klass: 'video_3' },
  14: { klass: link_klass.links },
};

const handmade_seller_filter_type_name = '핸드메이드 작가';

class HandmadeCreatorLandingBodyPC extends View<HandmadeCreatorLandingPageData> {
  scroll_smoother?: _ScrollSmoother;
  scroll_state: ScrollState = {
    current_index: 0,
    is_animating: false,
    is_scrollbar_moving: false,
  };

  header = new HandmadeCreatorHeader();
  motionSection = new MotionSection();
  scaleSection = new ScaleSection({ scroll_state: this.scroll_state });
  craftSection = new CraftSection({ scroll_state: this.scroll_state });
  videoSection = new VideoSection({ scroll_state: this.scroll_state });
  linksSection = new LinksSection();
  statisticsSection = new StatisticsSection();
  portFolioSection = new PortfolioSection();
  interviewSection = new InterviewSection();
  storySection = new StorySection();
  bottomSection = new BottomSection();
  questionSection = new QuestionSection();
  ctaButton = new ButtonCtaLanding({
    href: `/@/advance_reservation?category=${handmade_seller_filter_type_name}`,
    type: 'black',
    label: '지금 크리에이터 신청하기',
    klass: `${klass.submit} submit`,
  });

  floating_bottom_container = new FloatingBottomContainer({
    floating_box: { up: true, is_mobile: false },
    bottom_tab_bar: {
      on: false,
      selected_idx: -1,
    },
    app_info: this.data.layout_options.app_info,
    is_logged_in: !!this.data.layout_options.user,
    is_mobile: false,
  });

  protected override template({ layout_options }: HandmadeCreatorLandingPageData): Html {
    const { is_mobile } = layout_options;

    const body = html`
      <div class="${klass.page}">
        <!--        모션-->
        ${this.motionSection}

        <!--        스케일-->
        ${this.scaleSection}

        <!--        크래프트-->
        ${this.craftSection}

        <!--        비디오-->
        ${this.videoSection}

        <!--        링크-->
        ${this.linksSection}

        <!--        통계-->
        ${this.statisticsSection}

        <!--        포폴-->
        ${this.portFolioSection}

        <!--        인터뷰-->
        ${this.interviewSection}

        <!--        스토리-->
        ${this.storySection}

        <!--        바텀-->
        ${this.bottomSection}

        <!--        qna-->
        ${this.questionSection}
      </div>
    `;

    return html`
      <div>
        <!--        헤더-->
        ${this.header}
        <!--        랜딩페이지-->
        <div id="smooth-wrapper">
          <div id="smooth-content">
            ${new LayoutView(
              {
                body,
                footer_data: { ...footer_links, lang: ET.lang, is_mobile },
              },
              {
                require_top_arrow_button: false,
                full_width: true,
                has_bottom_tab_bar: false,
                selected_bottom_tab_idx: -1,
                ...layout_options,
              },
            )}
          </div>
        </div>
        <!--        cta-->
        ${this.ctaButton}
        <!--        floating button-->
        ${this.floating_bottom_container}
      </div>
    `;
  }

  @on('click', `.${floatKlass.floating_button}`)
  scrollTop() {
    this.scroll_smoother?.paused(false);
    this.scroll_state.is_scrollbar_moving = true;
    gsap.to(window, {
      duration: DEFAULT_ANIMATION_DURATION,
      scrollTo: { x: 0, y: 0 },
      onComplete: () => {
        this.scroll_state.is_scrollbar_moving = false;
        this.scroll_state.current_index = 0;
      },
    });
  }

  override onRender() {
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, ScrollSmoother);
    this.initScrollSmoother();
    this.initScrollObserver();
    this.scroll_smoother?.paused(true);

    window.addEventListener('load', () => {
      this.scroll_smoother?.paused(false);
      this.enableScrollBar();
    });

    let resizeTimeout: NodeJS.Timeout;

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        location.reload();
      }, 200);
    });

    this.addEventListener(NavigateToStorySectionCard, (e) => {
      const klass_name = `section_${e.detail.card_number + 1}`;
      gsap.to(this.scroll_smoother as _ScrollSmoother, {
        scrollTop: this.scroll_smoother?.offset(`.${story_klass[klass_name]}`),
        duration: DEFAULT_ANIMATION_DURATION,
      });
    });
  }

  override onMount() {
    const float_container = pipe(
      document.querySelector<HTMLElement>('.FloatingBottomContainer'),
      throwIf(isNull),
    );
    float_container.style.display = 'none';
  }

  scrollToPosition(isScrollingDown: boolean, duration: number = DEFAULT_ANIMATION_DURATION) {
    const scroll_index = isScrollingDown
      ? this.scroll_state.current_index + 1
      : this.scroll_state.current_index - 1;
    const extra_scroll = scroll_position_list[scroll_index]?.is_scrolled ? innerHeight : 0;
    const extra_duration = scroll_position_list[scroll_index]?.is_scrolled ? 0.25 : 0;

    this.scroll_state.is_animating = true;
    gsap.to(this.scroll_smoother as _ScrollSmoother, {
      scrollTop:
        (this.scroll_smoother?.offset(`.${scroll_position_list[scroll_index]?.klass}`) ?? 0) + extra_scroll,
      duration: duration + extra_duration,
      onComplete: () => {
        this.scroll_state.is_animating = false;
        this.scroll_state.current_index = scroll_index;
      },
    });
  }

  playAnimation(isScrollingDown: boolean) {
    this.scroll_state.is_animating = true;
    if (
      (this.scroll_state.current_index === ONE_SCROLL_INDEX_MAP_PC.initial_index && !isScrollingDown) ||
      (this.scroll_state.current_index === ONE_SCROLL_INDEX_MAP_PC.last_index && isScrollingDown)
    ) {
      this.scroll_smoother?.paused(false);
      this.scroll_state.is_animating = false;
      return;
    }

    this.scrollToPosition(isScrollingDown, DEFAULT_ANIMATION_DURATION);
  }

  initScrollSmoother() {
    this.scroll_smoother = ScrollSmoother.create({
      smooth: 1,
      effects: true,
      smoothTouch: 0.1,
    });
  }

  initScrollObserver() {
    ScrollTrigger.observe({
      type: 'wheel,touch',
      onUp: () => {
        if (this.scroll_smoother?.paused() && !this.scroll_state.is_animating) {
          this.playAnimation(true);
        }
      },
      onDown: () => {
        if (this.scroll_smoother?.paused() && !this.scroll_state.is_animating) {
          this.playAnimation(false);
        }
      },
      wheelSpeed: -1,
      tolerance: 10,
    });
  }

  @on(navigateSection)
  scrollToSection(e: navigateSection) {
    this.scroll_smoother?.paused(false);
    this.scroll_state.is_scrollbar_moving = true;
    gsap.to(window, {
      duration: 0.5,
      scrollTo: { y: `.${e.detail.klass}`, offsetY: e.detail.is_link_section ? -100 : 0 },
      onComplete: () => {
        this.scroll_state.is_scrollbar_moving = false;
      },
    });
  }

  @on(PauseScrollSmootherEvent)
  pauseScrollSmoother(e: PauseScrollSmootherEvent) {
    if (this.scroll_state.is_scrollbar_moving) return;
    this.scroll_smoother?.paused(e.detail.is_paused);
  }

  @on(SetScrollIndexEvent)
  setScrollIndexEvent(e: SetScrollIndexEvent) {
    this.scroll_state.current_index = e.detail.current_index;
  }

  enableScrollBar() {
    window.addEventListener('pointerdown', () => {
      this.scroll_smoother?.paused(false);
      this.scroll_state.is_scrollbar_moving = true;
    });

    window.addEventListener('pointerup', () => {
      if (this.scroll_state.current_index > 0 && this.scroll_state.current_index < 10) {
        this.scroll_smoother?.paused(true);
      }
      this.scroll_state.is_scrollbar_moving = false;
    });
  }
}

class HandmadeCreatorLandingBodyMO extends View<HandmadeCreatorLandingPageData> {
  scroll_smoother?: _ScrollSmoother;
  scroll_state: ScrollState = {
    current_index: 0,
    is_animating: false,
    is_scrollbar_moving: false,
  };

  header = new HandmadeCreatorHeader();
  motionSection = new MotionSection();
  scaleSection = new ScaleSection({ scroll_state: this.scroll_state });
  craftSection = new CraftSection({ scroll_state: this.scroll_state });
  videoSection = new VideoSection({ scroll_state: this.scroll_state });
  linksSection = new LinksSection();
  statisticsSection = new StatisticsSection();
  portFolioSection = new PortfolioSection();
  interviewSection = new InterviewSection();
  storySection = new StorySection();
  bottomSection = new BottomSection();
  questionSection = new QuestionSection();
  ctaButton = new ButtonCtaLanding({
    href: `/@/advance_reservation?category=${handmade_seller_filter_type_name}`,
    type: 'black',
    label: '지금 크리에이터 신청하기',
    klass: `${klass.submit} submit`,
  });

  floating_bottom_container = new FloatingBottomContainer(
    {
      floating_box: { up: true, is_mobile: false },
      bottom_tab_bar: {
        on: false,
        selected_idx: -1,
      },
      app_info: this.data.layout_options.app_info,
      is_logged_in: !!this.data.layout_options.user,
      is_mobile: false,
    },
    {
      bottom: '76px',
      right: '8px',
    },
  );

  animation_list = [
    {
      type: 'scale_heart',
      condition: (isScrollingDown: boolean) =>
        (this.scroll_state.current_index === ONE_SCROLL_INDEX_MAP_MO.scale_index && isScrollingDown) ||
        (this.scroll_state.current_index === ONE_SCROLL_INDEX_MAP_MO.scale_index + 1 && !isScrollingDown),
      effect: (isScrollingDown: boolean, duration: number) => this.scaleHeart(isScrollingDown, duration),
    },
    {
      type: 'change_background',
      condition: (isScrollingDown: boolean) =>
        (this.scroll_state.current_index === ONE_SCROLL_INDEX_MAP_MO.video_index && isScrollingDown) ||
        (this.scroll_state.current_index === ONE_SCROLL_INDEX_MAP_MO.video_index + 1 && !isScrollingDown),
      effect: (isScrollingDown: boolean, duration: number) =>
        this.changeBackground(isScrollingDown, duration),
    },
    {
      type: 'change_text',
      condition: (isScrollingDown: boolean) =>
        (this.scroll_state.current_index === ONE_SCROLL_INDEX_MAP_MO.video_index + 1 && isScrollingDown) ||
        (this.scroll_state.current_index === ONE_SCROLL_INDEX_MAP_MO.video_index + 2 && !isScrollingDown),
      effect: (isScrollingDown: boolean, duration: number) => this.changeText(isScrollingDown, duration),
    },
  ];

  protected override template({ layout_options }: HandmadeCreatorLandingPageData): Html {
    const { is_mobile } = layout_options;

    const body = html`
      <div class="${klass.page}">
        <!--        모션-->
        ${this.motionSection}

        <!--        스케일-->
        ${this.scaleSection}

        <!--        크래프트-->
        ${this.craftSection}

        <!--        비디오-->
        ${this.videoSection}

        <!--        링크-->
        ${this.linksSection}

        <!--        통계-->
        ${this.statisticsSection}

        <!--        포폴-->
        ${this.portFolioSection}

        <!--        인터뷰-->
        ${this.interviewSection}

        <!--        스토리-->
        ${this.storySection}

        <!--        바텀-->
        ${this.bottomSection}

        <!--        qna-->
        ${this.questionSection}
      </div>
    `;

    return html`
      <div>
        <!--        헤더-->
        ${this.header}
        <!--        랜딩페이지-->
        <div id="smooth-wrapper">
          <div id="smooth-content">
            ${new LayoutView(
              {
                body,
                footer_data: { ...footer_links, lang: ET.lang, is_mobile },
              },
              {
                require_top_arrow_button: false,
                full_width: true,
                has_bottom_tab_bar: false,
                selected_bottom_tab_idx: -1,
                ...layout_options,
              },
            )}
          </div>
        </div>
        <!--        cta-->
        ${this.ctaButton}
        <!--        floating button-->
        ${this.floating_bottom_container}
      </div>
    `;
  }

  @on('click', '.campaign_menu')
  @on('click', `.${floatKlass.floating_button}`)
  scrollTop() {
    this.scroll_smoother?.paused(false);
    this.scroll_state.is_scrollbar_moving = true;
    gsap.to(window, {
      duration: DEFAULT_ANIMATION_DURATION,
      scrollTo: { x: 0, y: 0 },
      onComplete: () => {
        this.scroll_state.is_scrollbar_moving = false;
        this.scroll_state.current_index = 0;
      },
    });
    this.scaleHeart(false, 0.1);
    this.changeBackground(false, 0.1);
    this.changeText(false, 0.1);
    this.header.closeMenu();
  }

  override onRender() {
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, ScrollSmoother);
    this.initScrollSmoother();
    this.initScrollObserver();

    window.addEventListener('load', () => {
      const page = pipe(
        document.querySelector<HTMLElement>('body.handmade-creator-landing '),
        throwIf(isNull),
      );
      page.classList.remove('scroll-block');
    });
  }

  override onMount() {
    const float_container = pipe(
      document.querySelector<HTMLElement>('.FloatingBottomContainer'),
      throwIf(isNull),
    );
    float_container.style.display = 'none';

    this.addEventListener(toggleHeaderMenu, (e) => {
      this.scroll_state.is_animating = e.detail.status;
    });
  }

  scrollToPosition(isScrollingDown: boolean, duration = DEFAULT_ANIMATION_DURATION) {
    const scroll_index = isScrollingDown
      ? this.scroll_state.current_index + 1
      : this.scroll_state.current_index - 1;

    this.scroll_state.is_animating = true;
    gsap.to(this.scroll_smoother as _ScrollSmoother, {
      scrollTop: this.scroll_smoother?.offset(`.${scroll_position_list_mo[scroll_index]?.klass}`),
      duration,
      onComplete: () => {
        this.scroll_state.is_animating = false;
        this.scroll_state.current_index = scroll_index;
      },
    });
  }

  /** 하트 이미지 스케일(scroll 방향에 따라 분기처리) */
  scaleHeart(isScrollingDown: boolean, duration = DEFAULT_ANIMATION_DURATION) {
    const scroll_index = isScrollingDown
      ? this.scroll_state.current_index + 1
      : this.scroll_state.current_index - 1;
    const tl = gsap.timeline({});
    this.scroll_state.is_animating = true;

    if (isScrollingDown) {
      tl.to(`.${scale_klass.heart_icon}`, { scale: 1, opacity: 1, duration })
        .to(`.${scale_klass.scale_title}`, { opacity: 0, duration }, '<')
        .to(
          `.${scale_klass.scale_desc}`,
          {
            opacity: 1,
            duration,
            onComplete: () => {
              this.scroll_state.is_animating = false;
              this.scroll_state.current_index = scroll_index;
            },
          },
          '<',
        );
    } else {
      tl.to(`.${scale_klass.heart_icon}`, { scale: 0, opacity: 0, duration })
        .to(`.${scale_klass.scale_title}`, { opacity: 1, duration }, '<')
        .to(
          `.${scale_klass.scale_desc}`,
          {
            opacity: 0,
            duration,
            onComplete: () => {
              this.scroll_state.is_animating = false;
              this.scroll_state.current_index = scroll_index;
            },
          },
          '<',
        );
    }
  }

  /** 비디오 섹션 배경 변경(scroll 방향에 따라 분기처리) */
  changeBackground(isScrollingDown: boolean, duration = DEFAULT_ANIMATION_DURATION) {
    const scroll_index = isScrollingDown
      ? this.scroll_state.current_index + 1
      : this.scroll_state.current_index - 1;
    const tl = gsap.timeline({});
    this.scroll_state.is_animating = true;

    if (isScrollingDown) {
      tl.fromTo(`.${video_klass.slogan}`, { scale: 1, duration }, { scale: 1.2 }).to(
        `.${video_klass.opacity}`,
        {
          opacity: 1,
          duration,
          onComplete: () => {
            this.scroll_state.is_animating = false;
            this.scroll_state.current_index = scroll_index;
          },
        },
        '<',
      );
    } else {
      tl.fromTo(`.${video_klass.slogan}`, { scale: 1.2, duration }, { scale: 1 }).to(
        `.${video_klass.opacity}`,
        {
          opacity: 0,
          duration,
          onComplete: () => {
            this.scroll_state.is_animating = false;
            this.scroll_state.current_index = scroll_index;
          },
        },
        '<',
      );
    }
  }

  /** 비디오 섹션 텍스트 변경(scroll 방향에 따라 분기처리) */
  changeText(isScrollingDown: boolean, duration = DEFAULT_ANIMATION_DURATION) {
    const scroll_index = isScrollingDown
      ? this.scroll_state.current_index + 1
      : this.scroll_state.current_index - 1;
    const tl = gsap.timeline({});
    this.scroll_state.is_animating = true;

    if (isScrollingDown) {
      tl.to(`.${video_klass.slogan}`, { opacity: 0, duration }).to(
        '.video_content',
        {
          opacity: 1,
          duration,
          onComplete: () => {
            this.scroll_state.is_animating = false;
            this.scroll_state.current_index = scroll_index;
          },
        },
        '<',
      );
    } else {
      tl.to(`.${video_klass.slogan}`, { opacity: 1, duration }).to(
        '.video_content',
        {
          opacity: 0,
          duration,
          onComplete: () => {
            this.scroll_state.is_animating = false;
            this.scroll_state.current_index = scroll_index;
          },
        },
        '<',
      );
    }
  }

  playAnimation(isScrollingDown: boolean) {
    this.scroll_state.is_animating = true;
    if (
      (this.scroll_state.current_index === ONE_SCROLL_INDEX_MAP_MO.initial_index && !isScrollingDown) ||
      (this.scroll_state.current_index === ONE_SCROLL_INDEX_MAP_MO.last_index && isScrollingDown)
    ) {
      this.scroll_smoother?.paused(false);
      this.scroll_state.is_animating = false;
      return;
    }

    const animation = pipe(
      this.animation_list,
      filter(({ condition }) => condition(isScrollingDown)),
      take(1),
      toArray,
    );

    if (animation[0]) {
      animation[0].effect(isScrollingDown, DEFAULT_ANIMATION_DURATION);
    } else {
      this.scrollToPosition(isScrollingDown, DEFAULT_ANIMATION_DURATION);
    }
  }

  initScrollSmoother() {
    this.scroll_smoother = ScrollSmoother.create({
      smooth: 1,
      effects: true,
      smoothTouch: 0.1,
    });
  }

  initScrollObserver() {
    ScrollTrigger.observe({
      type: 'wheel,touch',
      onUp: () => {
        if (this.scroll_smoother?.paused() && !this.scroll_state.is_animating) {
          this.playAnimation(true);
        }
      },
      onDown: () => {
        if (this.scroll_smoother?.paused() && !this.scroll_state.is_animating) {
          this.playAnimation(false);
        }
      },
      wheelSpeed: -1,
      tolerance: 10,
    });
  }

  @on(navigateSection)
  scrollToSection(e: navigateSection) {
    this.scroll_smoother?.paused(false);
    this.scroll_state.is_scrollbar_moving = true;
    gsap.to(window, {
      duration: 0.5,
      scrollTo: { y: `.${e.detail.klass}`, offsetY: 0 },
      onComplete: () => {
        this.scroll_state.is_scrollbar_moving = false;
        this.scroll_state.current_index = e.detail.should_initialize_animation
          ? ONE_SCROLL_INDEX_MAP_MO.initial_index
          : ONE_SCROLL_INDEX_MAP_MO.last_index;
      },
    });
    const isScrollingDown = !e.detail.should_initialize_animation;
    this.scaleHeart(isScrollingDown, 0.1);
    this.changeBackground(isScrollingDown, 0.1);
    this.changeText(isScrollingDown, 0.1);
  }

  @on(PauseScrollSmootherEvent)
  pauseScrollSmoother(e: PauseScrollSmootherEvent) {
    if (this.scroll_state.is_scrollbar_moving) return;
    this.scroll_smoother?.paused(e.detail.is_paused);
  }
}

export class HandmadeCreatorLandingPage extends Page<HandmadeCreatorLandingPageData> {
  protected override template({ layout_options }: HandmadeCreatorLandingPageData): Html {
    const { is_mobile } = layout_options;

    return is_mobile
      ? html`<div>${new HandmadeCreatorLandingBodyMO(this.data)}</div>`
      : html`<div>${new HandmadeCreatorLandingBodyPC(this.data)}</div>`;
  }
}
